import ComponentManager from '@/Management/ComponentManager';
import * as TypeInfo from '@/Utility/TypeInfo';
import Message from '@/Messages/Message';

export default class Messenger {

    /**
     * Ein Objekt welches die registrierten Callbacks beinhaltet
     */
    private static registrations = {};

    /**
     * Ein Objekt welches die registrierten Callbacks beinhaltet
     */
    private static callees = new Map();

    /**
     * Ein Objekt mit erzeugten Nachrichten
     */
    private static messages = {};

    /**
     * Registriert einen Callback für eine Message
     */
    private static registerCallbackForId(messageId, callback, callee) {
        Messenger.extendRegistrationIfNecessary(messageId, callee);

        Messenger.registrations[messageId].push(callback);

        let calleeRegistration = Messenger.callees.get(callee);
        calleeRegistration.push(callback);
        Messenger.callees.set(callee, calleeRegistration);
    }

    /**
     * Entfernt einen einzigen Callback zu einer Message aus der Registrierung 
     */
    private static unregisterCallbackFromMessage(messageId, callback) {
        Messenger.extendRegistrationIfNecessary(messageId);

        let index = Messenger.registrations[messageId].indexOf(callback);

        if (index > -1) {
            Messenger.registrations[messageId].splice(index, 1);
            return true;
        }
        return false;
    }

    /**
     * Entfernt alle Callbacks eines Callees aus der Registrierung 
     */
    private static unregisterCallbackFromCallee(messageId, callee) {
        Messenger.extendRegistrationIfNecessary(messageId, callee);

        let calleeRegistration = Messenger.callees.get(callee);
        let callbacksToRemove = [];

        for (let i = 0; i < calleeRegistration.length; i++) {
            let callback = calleeRegistration[i];

            if (Messenger.unregisterCallbackFromMessage(messageId, callback)) {
                callbacksToRemove.push(callback);
            }
        }

        callbacksToRemove.forEach((callback) => {
            Messenger.removeCallbackFromCallee(callback, calleeRegistration);
        });


        if (calleeRegistration.length > 0) {
            Messenger.callees.set(callee, calleeRegistration);
        } else {
            Messenger.callees.delete(callee);
        }
    }

    private static removeCallbackFromCallee(callback, calleeRegistration) {
        let index = calleeRegistration.indexOf(callback);

        if (index > -1) {
            calleeRegistration.splice(index, 1);
        }
    }

    /**
     * Ruft alle registrierten Callbacks einer Message auf und übergibt die Message als Argument
     */
    private static applyCallbacksForMessage(messageId, message) {
        Messenger.extendRegistrationIfNecessary(messageId);
        const callbacks = [];

        for (let i = 0; i < Messenger.registrations[messageId].length; i++) {
            let callback = Messenger.registrations[messageId][i];

            if (callback != null) {
                callbacks.push(callback);
            }
        }

        callbacks.forEach((callback) => {
            callback(message);
        });
        callbacks.length = 0;
    }

    /**
     * Erweitert das Registrierungs-Objekt um eine weitere Message-Id falls diese noch nicht im Objekt vorhanden ist
     */
    private static extendRegistrationIfNecessary(messageId, callee?) {
        if (Messenger.registrations[messageId] == null) {
            Messenger.registrations[messageId] = [];
        }

        if (callee != null && Messenger.callees.get(callee) == null) {
            Messenger.callees.set(callee, []);
        }
    }

    /**
     * Liefert eine Message anhand der Id
     */
    private static getMessageById(messageId) {
        return (ComponentManager as any).getMessage(messageId);
    }


    /**
     * Registriert einen Callback für eine Message
     */
    public static register<T extends Message = Message>(message: T, callback: (message: T) => void, callee) {
        let messageId = TypeInfo.getType(message);

        Messenger.registerCallbackForId(messageId, callback, callee);
    }

    /**
     * Registriert einen Callback für eine Message und leitet die letzte Message direkt an das callback weiter
     */
    public static registerAndReceive<T extends Message = Message>(Message: T, callback: (message: T) => void, callee) {
        Messenger.register(Message, callback, callee);

        let messageId = TypeInfo.getType(Message);
        let message = Messenger.messages[messageId];
        if (message != null && !message.isDisposed) {

            if (callback != null) {
                callback(message);
            }
        }
    }

    /**
     * Registriert einen Callback für eine Message-Id
     */
    public static registerForMessageId<T extends Message = Message>(messageId: number | string, callback: (message: T) => void, callee) {
        let Message = Messenger.getMessageById(messageId);

        if (Message != null) {
            Messenger.register(Message, callback, callee);
        }
    }

    /**
     * Registriert einen Callback für eine Message-Id und leitet die letzte Message direkt an das callback weiter
     */
    public static registerForMessageIdAndReceive<T extends Message = Message>(messageId: number | string, callback: (message: T) => void, callee) {
        let Message = Messenger.getMessageById(messageId);

        if (Message != null) {
            Messenger.registerAndReceive(Message, callback, callee);
        }
    }

    /**
     * Entfernt einen einzigen Callback oder alle Callbacks zu einer Message aus der Registrierung 
     */
    public static unregister<T extends Message = Message>(Message: T, callback: (message: T) => void, callee) {
        let messageId = TypeInfo.getType(Message);

        if (callback != null) {
            Messenger.unregisterCallbackFromMessage(messageId, callback);
        } else if (callee != null) {
            Messenger.unregisterCallbackFromCallee(messageId, callee);
        }
    }

    /**
     * Entfernt einen einzigen Callback oder alle Callbacks zu einer Message aus der Registrierung 
     */
    public static unregisterForMessageId<T extends Message = Message>(messageId: number | string, callback: (message: T) => void, callee) {
        let Message = Messenger.getMessageById(messageId);

        if (Message == null) {
            throw 'TypeException: Es konnte keine Message zur angegebenen Id gefunden werden.';
        }

        Messenger.unregister(Message, callback, callee);
    }

    /**
     * Sendet die Message an alle registrierten Callbacks
     */
    public static send<T extends Message = Message>(message: T) {
        let messageId = TypeInfo.getType(message);

        Messenger.applyCallbacksForMessage(messageId, message);
    }

    /**
     * Disposed eine zuvor erzeugte Message
     */
    public static disposeMessage(messageId: number | string) {
        if (messageId in Messenger.messages) {
            Messenger.messages[messageId].dispose();
            Messenger.messages[messageId] = null;
            delete Messenger.messages[messageId];
        }
    }

    /**
     * Erstellt eine neue Message anhand der Id
     */
    public static createMessage(messageId: number | string, params) {
        Messenger.disposeMessage(messageId);

        let Message = Messenger.getMessageById(messageId) as any;

        if (Message == null) {
            return null;
        }

        let message = new Message(params);
        Messenger.messages[messageId] = message;

        return message;
    }

    /**
     * Erstellt eine neue Message anhand der Id und sendet diese an alle registrierten Callbacks
     */
    public static createMessageAndSend(messageId: number | string, params?) {
        let message = Messenger.createMessage(messageId, params);

        if (message != null) {
            Messenger.send(message);
        }

        return message;
    }

}